import * as CANNON from 'cannon'

const FIXED_DT = 1 / 90
const MAX_STEPS = 900
const REST_STEPS = 20
const REST_LINEAR = 0.06
const REST_ANGULAR = 0.1

// The invisible wall the die is thrown into, matching the summoning ring.
// Long enough to feel weighty, short enough not to stall the turn.
const IDEAL_DURATION = 3.6

const RING_RADIUS = 4.15
const WALL_SEGMENTS = 14
const WALL_HEIGHT = 6

const mulberry32 = (seed) => {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) | 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

export const hashSeed = (value) => {
  const text = String(value)
  let h = 2166136261
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i)
    h = Math.imul(h, 16777619)
  }
  return h >>> 0
}

const addBody = (world, body) => {
  if (typeof world.addBody === 'function') world.addBody(body)
  else world.add(body)
}

/**
 * Deterministic rigid-body simulation of a d20 thrown into a walled ring.
 *
 * The die is a true convex polyhedron, so it tumbles and settles on a face the
 * way a real die does, bouncing off the floor and the ring's invisible sides.
 * Given identical seed and geometry the result is bit-identical every run,
 * which is what lets the visual roll be both physical and reproducible.
 */
export class DicePhysicsSim {
  constructor(geometry) {
    this.geometry = geometry
    this.world = null
    this.body = null
    this.divider = null
    this.half = 0
    this.build()
  }

  /**
   * Confines the die to one half of the ring (-1 left, +1 right, 0 whole ring).
   *
   * Advantage and disadvantage throw two dice, each simulated on its own, so a
   * divider is the cheapest way to guarantee they never pass through one
   * another while both still bounce around a full arena.
   */
  setArenaHalf(half = 0) {
    if (half === this.half) return
    this.half = half

    if (this.divider) {
      if (typeof this.world.removeBody === 'function') this.world.removeBody(this.divider)
      else this.world.remove(this.divider)
      this.divider = null
    }

    if (!half) return

    const wall = new CANNON.Body({ mass: 0, material: this.wallMaterial })
    wall.addShape(new CANNON.Plane())
    // Plane normal is +Z; face it along +X or -X so it walls off the midline.
    wall.quaternion.setFromEuler(0, (half > 0 ? 1 : -1) * (Math.PI / 2), 0, 'YXZ')
    addBody(this.world, wall)
    this.divider = wall
  }

  build() {
    const { vertices, faces, scale } = this.geometry

    const world = new CANNON.World()
    world.gravity.set(0, -34, 0)
    world.broadphase = new CANNON.NaiveBroadphase()
    world.solver.iterations = 14
    world.allowSleep = false

    const diceMaterial = new CANNON.Material('dice')
    const stoneMaterial = new CANNON.Material('stone')
    const wallMaterial = new CANNON.Material('wall')

    // Low floor friction keeps horizontal speed alive long enough for the die to
    // carry across the circle and strike the wall rather than stopping short.
    world.addContactMaterial(new CANNON.ContactMaterial(diceMaterial, stoneMaterial, {
      friction: 0.11,
      restitution: 0.42
    }))
    world.addContactMaterial(new CANNON.ContactMaterial(diceMaterial, wallMaterial, {
      friction: 0.18,
      restitution: 0.62
    }))

    const floor = new CANNON.Body({ mass: 0, material: stoneMaterial })
    floor.addShape(new CANNON.Plane())
    floor.quaternion.setFromAxisAngle(new CANNON.Vec3(1, 0, 0), -Math.PI / 2)
    addBody(world, floor)

    // A ring of inward-facing planes: the die rebounds off the rune circle
    // instead of sliding away into the dark.
    for (let i = 0; i < WALL_SEGMENTS; i++) {
      const angle = (i / WALL_SEGMENTS) * Math.PI * 2
      const wall = new CANNON.Body({ mass: 0, material: wallMaterial })
      wall.addShape(new CANNON.Plane())
      wall.position.set(
        Math.cos(angle) * RING_RADIUS,
        WALL_HEIGHT / 2,
        Math.sin(angle) * RING_RADIUS
      )
      // A plane's normal is +Z; this turns it to face inward, toward the centre.
      wall.quaternion.setFromEuler(0, -angle - Math.PI / 2, 0, 'YXZ')
      addBody(world, wall)
    }

    const points = vertices.map((v) => new CANNON.Vec3(v[0] * scale, v[1] * scale, v[2] * scale))
    const shape = new CANNON.ConvexPolyhedron(points, faces.map((f) => [...f]))

    const body = new CANNON.Body({ mass: 1, material: diceMaterial })
    body.addShape(shape)
    body.linearDamping = 0.015
    body.angularDamping = 0.06
    addBody(world, body)

    this.world = world
    this.body = body
    this.wallMaterial = wallMaterial
  }

  reset(rand) {
    const body = this.body

    // Thrown from one side hard enough to cross the ring and rebound off the
    // far wall, so the throw actually uses the space instead of dropping in place.
    // Constrained to its own half when two dice share the ring.
    const entryAngle = this.half
      ? (this.half > 0 ? 0 : Math.PI) + (rand() - 0.5) * Math.PI * 0.8
      : rand() * Math.PI * 2
    const entryRadius = RING_RADIUS * (0.42 + rand() * 0.2)

    body.position.set(
      Math.cos(entryAngle) * entryRadius,
      1.7 + rand() * 1.1,
      Math.sin(entryAngle) * entryRadius
    )

    // Aimed across the circle, fanned a little so throws are not all diametric.
    const aim = entryAngle + Math.PI + (rand() - 0.5) * 0.9
    const speed = 14 + rand() * 6

    body.velocity.set(
      Math.cos(aim) * speed,
      -1.2 - rand() * 2,
      Math.sin(aim) * speed
    )

    body.angularVelocity.set(
      (rand() * 2 - 1) * 26,
      (rand() * 2 - 1) * 26,
      (rand() * 2 - 1) * 26
    )

    body.quaternion.setFromEuler(
      rand() * Math.PI * 2,
      rand() * Math.PI * 2,
      rand() * Math.PI * 2,
      'XYZ'
    )

    body.force.setZero()
    body.torque.setZero()
    body.initPosition.copy(body.position)
    body.wakeUp()
  }

  /** Number showing on top for the body's current orientation. */
  restingFace() {
    const { faceNormals, faceNumbers } = this.geometry
    const q = this.body.quaternion
    let best = -Infinity
    let number = faceNumbers[0]

    faceNormals.forEach((normal, index) => {
      const v = new CANNON.Vec3(normal.x, normal.y, normal.z)
      const out = q.vmult(v)
      if (out.y > best) {
        best = out.y
        number = faceNumbers[index]
      }
    })

    return { number, upness: best }
  }

  /** Runs one throw to rest, recording every step for playback. */
  simulate(seed) {
    const rand = mulberry32(seed)
    this.reset(rand)

    const frames = []
    let settled = 0
    let maxRadius = 0

    for (let step = 0; step < MAX_STEPS; step++) {
      this.world.step(FIXED_DT)

      const p = this.body.position
      const q = this.body.quaternion
      frames.push([p.x, p.y, p.z, q.x, q.y, q.z, q.w])

      maxRadius = Math.max(maxRadius, Math.hypot(p.x, p.z))

      const linear = this.body.velocity.norm()
      const angular = this.body.angularVelocity.norm()

      settled = linear < REST_LINEAR && angular < REST_ANGULAR ? settled + 1 : 0
      if (settled >= REST_STEPS) break
    }

    const rest = this.restingFace()

    return {
      frames,
      dt: FIXED_DT,
      duration: frames.length * FIXED_DT,
      restingNumber: rest.number,
      upness: rest.upness,
      maxRadius,
      hitWall: maxRadius > RING_RADIUS - 1.0
    }
  }

  /**
   * Finds a throw that genuinely comes to rest on `targetNumber`.
   *
   * Rejection sampling keeps the physics honest: nothing is steered or snapped,
   * we simply keep throwing (deterministically, off the seed) until the die
   * lands on the number the backend rolled.
   */
  rollFor(targetNumber, seed, maxAttempts = 160) {
    const base = hashSeed(seed)
    let best = null
    let landedAny = null
    let fallback = null

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const result = this.simulate(hashSeed(`${base}:${attempt}`))
      const landed = result.restingNumber === targetNumber && result.upness > 0.9

      if (landed) {
        if (!landedAny) landedAny = { ...result, attempts: attempt + 1 }

        // Ideal throw: correct face, rebounds off the ring, and does not
        // outstay its welcome.
        if (result.hitWall && result.duration <= IDEAL_DURATION) {
          return { ...result, attempts: attempt + 1, exact: true }
        }

        if (result.hitWall && (!best || result.duration < best.duration)) {
          best = { ...result, attempts: attempt + 1 }
        }
      }

      if (!fallback) fallback = result
    }

    if (best) return { ...best, exact: true }
    if (landedAny) return { ...landedAny, exact: true }

    // Never observed in practice, but the shown face must match the backend.
    return { ...fallback, attempts: maxAttempts, exact: false }
  }
}

export default DicePhysicsSim
